import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
	TurnstileAccessFilterOptions,
	TurnstileAccessFilterOptionsSchema,
	TurnstileAccessResponseSchema,
	TurnstileAccessType,
} from '@/constants/TurnstileAccessConstants';
import api from '@/services/apiSgft';
import { TurnstileAccessFilterAtom } from '@/state/TurnstileAccessFilter.atom';
import { removeTimeZone } from '@/utils/formatDate';

interface UseTurnstileAccessDataParams {
	filteredData: TurnstileAccessFilterAtom;
	searchInput: string;
}

export const useTurnstileAccessData = ({
	filteredData,
	searchInput,
}: UseTurnstileAccessDataParams) => {
	const fetchTurnstileAccessFilters = async () => {
		try {
			const response = await api.get('turnstile-access/filters', {
				params: {
					type: filteredData.type,
					date: removeTimeZone(new Date()),
					management: filteredData.management,
					workstation: filteredData.workstation,
					pole: filteredData.pole,
					board: filteredData.board,
					jobTitle: filteredData.jobTitle,
					coordination: filteredData.coordination,
					employee: filteredData.employee,
					employeeStatus: filteredData.employeeStatus,
					training: filteredData.training,
					showAllEmployees: filteredData.showAllEmployees,
				},
			});
			const parsedData = TurnstileAccessFilterOptionsSchema.parse(
				response.data,
			);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const { data: filterOptions, isLoading: isLoadingFilters } =
		useQuery<TurnstileAccessFilterOptions>(
			['turnstile-access-filters', filteredData],
			fetchTurnstileAccessFilters,
		);

	const fetchTurnstileAccess = async ({ pageParam = 1 }) => {
		try {
			const response = await api.get('turnstile-access', {
				params: {
					type: TurnstileAccessType.Blocked,
					page: pageParam,
					size: 10,
					date: removeTimeZone(new Date()),
					search: searchInput,
					management: filteredData.management,
					workstation: filteredData.workstation,
					pole: filteredData.pole,
					board: filteredData.board,
					jobTitle: filteredData.jobTitle,
					coordination: filteredData.coordination,
					employee: filteredData.employee,
					employeeStatus: filteredData.employeeStatus,
					training: filteredData.training,
					showAllEmployees: filteredData.showAllEmployees,
				},
			});
			const parsedData = TurnstileAccessResponseSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const fetchInRiskTurnstileAccess = async ({ pageParam = 1 }) => {
		try {
			const response = await api.get('turnstile-access', {
				params: {
					type: TurnstileAccessType.InRisk,
					page: pageParam,
					size: 10,
					date: removeTimeZone(new Date()),
					search: searchInput,
					management: filteredData.management,
					workstation: filteredData.workstation,
					pole: filteredData.pole,
					board: filteredData.board,
					jobTitle: filteredData.jobTitle,
					coordination: filteredData.coordination,
					employee: filteredData.employee,
					employeeStatus: filteredData.employeeStatus,
					training: filteredData.training,
					showAllEmployees: filteredData.showAllEmployees,
				},
			});
			const parsedData = TurnstileAccessResponseSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const fetchExceptionTurnstileAccess = async ({ pageParam = 1 }) => {
		try {
			const response = await api.get('turnstile-access', {
				params: {
					type: TurnstileAccessType.Exception,
					page: pageParam,
					size: 10,
					date: removeTimeZone(new Date()),
					search: searchInput,
					management: filteredData.management,
					workstation: filteredData.workstation,
					pole: filteredData.pole,
					board: filteredData.board,
					jobTitle: filteredData.jobTitle,
					coordination: filteredData.coordination,
					employee: filteredData.employee,
					employeeStatus: filteredData.employeeStatus,
					training: filteredData.training,
					showAllEmployees: filteredData.showAllEmployees,
				},
			});
			const parsedData = TurnstileAccessResponseSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const {
		data: blockedData,
		isLoading: isLoadingBlocked,
		fetchNextPage: fetchNextPageBlocked,
		isFetchingNextPage: isFetchingNextPageBlocked,
		hasNextPage: hasNextPageBlocked,
	} = useInfiniteQuery(
		['blocked-turnstile-access', filteredData, searchInput],
		fetchTurnstileAccess,
		{
			getNextPageParam: (lastPage) => lastPage.nextPage,
		},
	);

	const {
		data: inRiskData,
		isLoading: isLoadingInRisk,
		fetchNextPage: fetchNextPageInRisk,
		isFetchingNextPage: isFetchingNextPageInRisk,
		hasNextPage: hasNextPageInRisk,
	} = useInfiniteQuery(
		['in-risk-turnstile-access', filteredData, searchInput],
		fetchInRiskTurnstileAccess,
		{
			getNextPageParam: (lastPage) => lastPage.nextPage,
		},
	);

	const {
		data: exceptionData,
		isLoading: isLoadingException,
		fetchNextPage: fetchNextPageException,
		isFetchingNextPage: isFetchingNextPageException,
		hasNextPage: hasNextPageException,
	} = useInfiniteQuery(
		['exception-turnstile-access', filteredData, searchInput],
		fetchExceptionTurnstileAccess,
		{
			getNextPageParam: (lastPage) => lastPage.nextPage,
		},
	);

	const blockedEmployees = blockedData?.pages.flatMap((page) =>
		page?.turnstileAccess?.employees?.flat(),
	);
	const blockedCount = blockedData?.pages[0]?.count || 0;

	const inRiskEmployees = inRiskData?.pages.flatMap((page) =>
		page?.turnstileAccess?.employees?.flat(),
	);
	const inRiskCount = inRiskData?.pages[0]?.count || 0;

	const exceptionEmployees = exceptionData?.pages.flatMap((page) =>
		page?.turnstileAccess?.employees?.flat(),
	);
	const exceptionCount = exceptionData?.pages[0]?.count || 0;

	return {
		// Filter data
		filterOptions,
		isLoadingFilters,

		// Blocked data
		blockedEmployees,
		blockedCount,
		isLoadingBlocked,
		fetchNextPageBlocked,
		isFetchingNextPageBlocked,
		hasNextPageBlocked,

		// In risk data
		inRiskEmployees,
		inRiskCount,
		isLoadingInRisk,
		fetchNextPageInRisk,
		isFetchingNextPageInRisk,
		hasNextPageInRisk,

		// Exception data
		exceptionEmployees,
		exceptionCount,
		isLoadingException,
		fetchNextPageException,
		isFetchingNextPageException,
		hasNextPageException,
	};
};
